import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, getEventById, getEventInfosByEventId, getTicketTypesByEventId, getTicketTypeInfosByTicketTypeId, getTicketTypeNumberOfAvailableTickets } from "ticketino-api-client";
import axios from "axios";

import Header from "./components/Header";

import imgIconAdd from "../images/icons/add.svg";
import imgIconRemove from "../images/icons/remove.svg";


const Workshops = () => {
    const [token] = useState(sessionStorage.getItem("token"));

    const [orderId] = useState(sessionStorage.getItem("orderId"));
    const [order, setOrder] = useState({});

    const [eventId, setEventId] = useState();
    const [workshopEventIds, setWorkshopEventIds] = useState([]);

    // one entry per workshop event, each with its own info and ticketTypes
    const [events, setEvents] = useState([]);

    const [selectedTicketQuantities, setSelectedTicketQuantities] = useState([]);

    // ticket type ids whose description has been expanded by the user (descriptions start closed)
    const [openDescriptions, setOpenDescriptions] = useState([]);

    // all ticket types across the workshop events
    const ticketTypes = events.flatMap(evt => evt.ticketTypes ?? []);


    // react hook for navigation
    let navigate = useNavigate();

    // base url
    let baseUrl = process.env.REACT_APP_BASEURL_API;

    // setting the base url of the npm package api calls
    api.defaults.baseURL = baseUrl;

    // fetching resources
    const [resources, setResources] = useState({});

    // fetching params
    let { language } = useParams();

    let languageId = 0;

    // changing languageId according to the url
    switch (language) {
        case ("de" || "DE"):
            languageId = 1;
            break;
        case ("en" || "EN"):
            languageId = 3;
            break;
        default:
            languageId = 0;
            language = "de";
            break;
    }

    useEffect(() => {
        requestFormSettings();
    }, []);

    useEffect(() => {
        setEvents([]);
    }, []);

    useEffect(() => {
        requestResources();
    }, [language]);

    useEffect(() => {
        axios.defaults.headers.common["Authorization"] = "Bearer " + token;
        api.defaults.headers.common['Authorization'] = "Bearer " + token;

        if (orderId && workshopEventIds.length > 0) {
            loadOrder(orderId);
            loadEvents(workshopEventIds, orderId)
        }
    }, [orderId, workshopEventIds]);

    // the workshops are only added to the order on "next", so any workshop ticket still in the
    // order (e.g. the user came back from the address step) has to be cleared to avoid duplicates
    useEffect(() => {
        if (events.length > 0) {
            const performTicketCleanup = async () => {
                if (orderId) {
                    const ticketsDeleted = await deleteOrderTickets(orderId);
                    if (ticketsDeleted) await loadOrder(orderId);
                }
            };

            setSelectedTicketQuantities([]);
            performTicketCleanup();
        }
    }, [events]);

    const requestFormSettings = async () => {
        await axios.get(`form/formsettings`).then((res) => {
            setEventId(res.data.eventId);

            setWorkshopEventIds([
                res.data.eventIdWorkshopsSpecials,
                res.data.eventIdWorkshopsMasterclass
            ].filter(Boolean));
        });
    };

    const requestResources = async () => {
        await axios
            .get(`form/resources/${language}`)
            .then((res) => {
                setResources(res.data?.translation);
            })
            .catch((error) => console.error(error.response.data));
    };

    const loadOrder = async (orderId) => {
        try {
            const updatedOrder = await getOrderByOrderId(orderId);
            setOrder(updatedOrder);

            // without an entrance ticket there is nothing to add workshops to
            if (!updatedOrder?.tickets || updatedOrder.tickets.length === 0) {
                navigate(`/${language}/home`);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const getOrderByOrderId = async (orderId) => {
        try {
            const res = await axios.get(`${baseUrl}/ShopBasket/Order/${orderId}`);
            return res.data;
        }
        catch (error) {
            console.error(error);
        }
    }

    const loadEvents = async (eventIds, orderId) => {
        try {
            const loadedEvents = await Promise.all(
                eventIds.map(async (workshopEventId) => {
                    // Event
                    const event = await getEventById(workshopEventId);
                    const infos = await getEventInfosByEventId(workshopEventId);

                    // sorting it according to the language
                    const eventInfo = infos.eventInfos.find(info => info.languageId == languageId);

                    // TicketType
                    const ticketTypes = await getTicketTypesByEventId(workshopEventId);

                    const updatedTicketTypes = await Promise.all(
                        ticketTypes.map(async (ticketType) => {
                            const info = await getTicketTypeInfosByTicketTypeId(ticketType.id);

                            // sorting it according to the language
                            const ticketTypeInfo = info.find(info => info.languageId == languageId) ?? info[0];

                            const ticketTypeAvailability = await getTicketTypeNumberOfAvailableTickets(ticketType.id, orderId);

                            // setting the ticketTypeInfo and ticketTypeAvailability object as attributes in ticketType (info and availability)
                            return { ...ticketType, info: ticketTypeInfo, availability: ticketTypeAvailability }
                        })
                    )

                    // setting the eventInfo and its ticket types in the attributes of event (info and ticketTypes)
                    return { ...event, info: eventInfo, ticketTypes: updatedTicketTypes };
                })
            )

            // setting the values in the end
            setEvents(loadedEvents);

        } catch (error) {
            console.error(error);
        }
    }

    const deleteOrderTickets = async () => {
        try {
            const response = await axios.get(`${baseUrl}/ShopBasket/Order/${orderId}`);
            const tickets = response.data.tickets;

            if (tickets.length > 0) {
                const ticketTypeIds = ticketTypes.map(tt => tt.id);
                const ticketsInOrder = tickets
                    .filter(ticket => ticketTypeIds.includes(ticket.ticketTypeId))
                    .map(ticket => ticket.id);

                if (ticketsInOrder.length > 0) {
                    const body = { TicketsToRemove: ticketsInOrder };
                    await axios.delete(`${baseUrl}/ShopBasket/Order/${orderId}/Tickets`, {
                        headers: { Accept: "application/json" },
                        data: body,
                    });

                    return true; // Tickets were deleted
                }
            }

            return false; // No relevant tickets to delete
        } catch (error) {
            console.error("Failed to delete order tickets:", error);
        }
    };

    // the selection is kept locally and sent to the order in a single call when "next" is clicked
    const addTicketsToBasket = async () => {
        const ticketsToAdd = selectedTicketQuantities
            .filter(item => item.quantity > 0)
            .map(item => {
                const ticketType = ticketTypes.find(tt => tt.id === item.id);

                return {
                    ticketTypeId: item.id,
                    quantity: item.quantity,
                    option10: ticketType ? (ticketType.price + 15.00).toFixed(2) : ""
                };
            });

        if (ticketsToAdd.length === 0) return true;

        try {
            await axios.post(`${baseUrl}/ShopBasket/Order/${orderId}/Tickets`, { ticketsToAdd });
            return true;
        } catch (error) {
            console.error(error.response?.data);
            return false;
        }
    };

    const onSubmit = async () => {
        const added = await addTicketsToBasket();
        if (!added) return;

        // gtm - addToCart
        //window.dataLayer = window.dataLayer || [];
        //window.dataLayer.push({
        //    event: 'addToCart',
        //});

        navigate(`/${language}/address`);
    };

    const getTicketQuantity = (ticketTypeId) =>
        selectedTicketQuantities.find(item => item.id === ticketTypeId)?.quantity ?? 0;

    const incrementTicket = (ticketTypeId, max) => {
        if (getTicketQuantity(ticketTypeId) >= max) return;

        setSelectedTicketQuantities(prev => {
            const foundIndex = prev.findIndex(item => item.id === ticketTypeId);

            if (foundIndex === -1) {
                return [...prev, { id: ticketTypeId, quantity: 1 }];
            }

            const updated = [...prev];
            updated[foundIndex] = { ...updated[foundIndex], quantity: updated[foundIndex].quantity + 1 };
            return updated;
        });
    };

    const decrementTicket = (ticketTypeId) => {
        setSelectedTicketQuantities(prev => {
            const foundIndex = prev.findIndex(item => item.id === ticketTypeId);
            if (foundIndex === -1) return prev;

            const updated = [...prev];
            updated[foundIndex] = { ...updated[foundIndex], quantity: Math.max(updated[foundIndex].quantity - 1, 0) };
            return updated;
        });
    };

    const isDescriptionOpen = (ticketTypeId) => openDescriptions.includes(ticketTypeId);

    const toggleDescription = (ticketTypeId) => {
        setOpenDescriptions(prev => prev.includes(ticketTypeId)
            ? prev.filter(id => id !== ticketTypeId)
            : [...prev, ticketTypeId]
        );
    };

    const formatDate = (isoString) => {
        if (!isoString) return "";

        const date = new Date(isoString);
        const weekday = date.toLocaleDateString(language, { weekday: 'long' });
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based

        return `${weekday}, ${day}.${month}.${date.getFullYear()}`;
    };

    const formatTime = (isoString) => {
        const date = new Date(isoString);
        return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    };

    const formatTimeRange = (start, end) => {
        if (!start) return "";
        return end ? `${formatTime(start)} - ${formatTime(end)}` : formatTime(start);
    };

    const mapTicketTypes = () => {
        if (events.length === 0) return false;

        return events.map((evt) => {
            const visibleTickets = [...(evt.ticketTypes ?? [])]
                .filter(tt => tt.availability?.status === 0 || tt.availability?.status === 1)
                .sort((a, b) => a.sortOrder - b.sortOrder);

            if (visibleTickets.length === 0) return null;

            console.log(evt)

            return (
                <div className="mb-5" key={evt.id}>
                    {evt.info?.bannerImagePath &&
                        <div className="row mb-4 ticket-row">
                            <div className="col-md-12">
                                <img className="event-banner" src={evt.info.bannerImagePath} alt={evt.info?.name} />
                            </div>
                        </div>
                    }

                    <div className="row mb-3 ticket-row">
                        <div className="col-md-12">
                            <span className="text-title">{evt.info?.name}</span>
                        </div>
                        <div className="col-md-12">
                            <span>{evt.info?.shortDescription}</span>
                        </div>
                    </div>

                    {visibleTickets.map((tt) => (
                        <div className="mb-4 ticket-row" key={tt.id}>
                            <div className="row align-items-start">
                                <div className="col-10">
                                    <label className="text-subtitle text-uppercase text-pink">{tt.info?.name}</label>
                                </div>

                                <div className="col-2 text-end">
                                    {tt.info?.description &&
                                        <button
                                            type="button"
                                            className="btn-counter cursor-pointer"
                                            onClick={() => toggleDescription(tt.id)}
                                            aria-expanded={isDescriptionOpen(tt.id)}
                                            aria-controls={`ticket-description-${tt.id}`}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                                                fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points={isDescriptionOpen(tt.id) ? "6 15 12 9 18 15" : "6 9 12 15 18 9"} />
                                            </svg>
                                        </button>
                                    }
                                </div>
                            </div>

                            {tt.info?.description && isDescriptionOpen(tt.id) &&
                                <div className="row mt-1" id={`ticket-description-${tt.id}`}>
                                    <div className="col-md-12">
                                        <span className="ticket-description">{tt.info.description}</span>
                                    </div>
                                </div>
                            }

                            <div className="row align-items-center mt-4">
                                <div className="col-4">
                                    <label>{formatDate(tt.start)}</label>
                                </div>

                                <div className="col-3">
                                    <label className="text-nowrap">{formatTimeRange(tt.start, tt.end)}</label>
                                </div>

                                <div className="col-3">
                                    <label className="text-nowrap">
                                        {tt.currency} {(tt.price + 15).toFixed(2)}
                                    </label>
                                </div>

                                <div className="col-2">
                                    {tt.availability?.availableTickets === 0 ? (
                                        <span className="text-soldout">{resources?.SoldOut}</span>
                                    ) : (
                                        <div className="d-flex align-items-center gap-2">
                                            <button
                                                type="button"
                                                className="btn-counter"
                                                onClick={() => decrementTicket(tt.id)}
                                                disabled={getTicketQuantity(tt.id) <= 0}
                                            >
                                                <img src={imgIconRemove} alt="Decrease" width="16" height="16" />
                                            </button>

                                            <span className="ticket-quantity">{getTicketQuantity(tt.id)}</span>

                                            <button
                                                type="button"
                                                className="btn-counter"
                                                onClick={() => incrementTicket(tt.id, tt.availability?.availableTickets)}
                                                disabled={getTicketQuantity(tt.id) >= tt.availability?.availableTickets}
                                            >
                                                <img src={imgIconAdd} alt="Increase" width="16" height="16" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            
                        </div>
                    ))}
                </div>
            );
        });
    };

    const onBackClick = async () => {
        navigate(-1)
    }

    return (
        <>
            <Header language={language} pagePath="workshops-saturday" />
            <div className="page-container">

                <div className="container wrapper">
                    {/* intro */}
                    <div className="row">
                        <div className="col-md-12">
                            <h1 className="text-title">{resources?.HomeTitle}</h1>
                            <div className="mt-4 home-description" dangerouslySetInnerHTML={{ __html: resources?.HomeDescription }}></div>
                        </div>
                    </div>

                    <div className="row mt-5">
                        <div className="col-md-12">
                            {mapTicketTypes()}
                        </div>
                    </div>

                    <div className="row mt-4">
                        <div className="col-12 text-end">
                            <button
                                className="btn btn-custom"
                                onClick={() => onSubmit()}
                            >
                                {resources?.Next}
                            </button>
                        </div>
                    </div>

                    <div className="row mt-5">
                        <div className="col-md-12 text-center">
                            <p className="mt-4">
                                <small className="powered-by">powered by <a href="https://organizer.ticketino.com/de" target="_blank">TICKETINO</a></small>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Workshops